import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Back, Play } from '../components/Icons';
import { APP_LANGS, fmt, useT } from '../lib/i18n';
import { session, useSession } from '../lib/session';
import { DEFAULT_FORMAT, updateSettings, useSettings, type AppLang, type SfxName } from '../lib/settings';
import { playSfx, unlockAudio } from '../lib/sfx';
import { EdgeHint } from '../components/Voice';
import { useVoices, VOICE_LANGS } from '../lib/languages';
import { isNaturalVoice, pickVoice } from '../lib/tts';


const SFX_OPTIONS: Record<'gift' | 'follow' | 'share' | 'chat', SfxName[]> = {
  gift: ['coin', 'bell', 'off'],
  follow: ['pop', 'bell', 'off'],
  share: ['whoosh', 'pop', 'off'],
  chat: ['off', 'tick', 'pop'],
};

const SFX_COLOR = { gift: '#FFB23F', follow: '#5BE3B0', share: '#7CC0FF', chat: '#A8A8B3' } as const;


export function Settings() {
  const t = useT();
  const s = useSettings();
  const voices = useVoices();
  const sp = useSession().speech;
  const [copied, setCopied] = useState<'feed' | 'killfeed' | null>(null);
  const [lastUser] = useState(() => session.get().username);

  const family = s.voiceLang.split('-')[0];
  const langVoices = useMemo(() => voices.filter((v) => v.lang.toLowerCase().replace('_', '-').startsWith(family)), [voices, family]);
  const otherVoices = useMemo(() => voices.filter((v) => !langVoices.includes(v)), [voices, langVoices]);
  const best = pickVoice(s.voiceLang, null, voices);

  const setLang = (appLang: AppLang) => {
    const wasDefault = Object.values(DEFAULT_FORMAT).includes(s.format);
    updateSettings({ appLang, format: wasDefault ? DEFAULT_FORMAT[appLang] : s.format });
  };

  const test = () => {
    unlockAudio();
    session.tts.test(t.testSentence, s.voiceLang);
  };

  const overlayUrl = `${location.origin}/overlay?u=${lastUser ?? 'username'}&chats=1&gifts=1&follows=1&likes=0&joins=0&size=${s.fontSize}`;
  const killfeedUrl = `${location.origin}/overlay/killfeed?u=${lastUser ?? 'username'}`;
  const copy = async (url: string, which: 'feed' | 'killfeed') => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(which);
      window.setTimeout(() => setCopied(null), 1500);
    } catch {
      /* clipboard blocked */
    }
  };

  const rateLabel = `${s.rate.toFixed(1)}×`;
  const pitchLabel = s.pitch < 0.9 ? t.low : s.pitch > 1.1 ? t.high : t.normal;

  return (
    <div className="screen" style={{ paddingTop: 12, gap: 28 }}>
      <header className="settings__head">
        <Link to={lastUser && session.get().status !== 'idle' ? `/live/${lastUser}` : '/'} className="iconbtn" aria-label={t.back}>
          <Back />
        </Link>
        <h1>{t.settings}</h1>
      </header>

      <section className="stack">
        <h2 className="section-title">{t.language}</h2>
        <label htmlFor="applang" className="label" style={{ color: 'var(--text)', fontSize: 15 }}>
          {t.appLanguage}
        </label>
        <select id="applang" className="sel" value={s.appLang} onChange={(e) => setLang(e.target.value as AppLang)}>
          {APP_LANGS.map((l) => (
            <option key={l.value} value={l.value}>
              {l.label}
            </option>
          ))}
        </select>
        <fieldset style={{ border: 0, margin: '8px 0 0', padding: 0 }} className="stack">
          <legend style={{ padding: 0, marginBottom: 8, fontSize: 15, fontWeight: 600 }}>{t.voiceLanguage}</legend>
          <div className="opts">
            {VOICE_LANGS.map((v) => (
              <label key={v.value} className="opt">
                <input type="radio" name="vl" checked={s.voiceLang === v.value} onChange={() => updateSettings({ voiceLang: v.value, voiceURI: null })} />
                <span>
                  <span className="opt__main">{v.label}</span>
                  <span className="opt__sub">{v.sub}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        <label className="row row--sm card" style={{ padding: '4px 16px' }}>
          <span>
            <span className="row__title">{t.detectLanguage}</span>
            <span className="row__sub">{t.detectLanguageSub}</span>
          </span>
          <input className="sw" type="checkbox" role="switch" checked={s.detectLanguage} onChange={(e) => updateSettings({ detectLanguage: e.target.checked })} />
        </label>
      </section>

      <section className="stack">
        <h2 className="section-title">{t.voice}</h2>
        <label htmlFor="voice" className="label" style={{ color: 'var(--text)', fontSize: 15 }}>
          {t.voice}
        </label>
        <select id="voice" className="sel" value={s.voiceURI ?? ''} onChange={(e) => updateSettings({ voiceURI: e.target.value || null })}>
          <option value="">{t.bestVoice}{best ? ` · ${best.name}` : ''}</option>
          {langVoices.map((v) => (
            <option key={v.voiceURI} value={v.voiceURI}>
              {v.name} · {v.lang}
              {isNaturalVoice(v) ? ' ★' : ''}
            </option>
          ))}
          {otherVoices.length > 0 && (
            <optgroup label={t.deviceVoices}>
              {otherVoices.map((v) => (
                <option key={v.voiceURI} value={v.voiceURI}>
                  {v.name} · {v.lang}
                </option>
              ))}
            </optgroup>
          )}
        </select>
        {langVoices.length === 0 && voices.length > 0 && <p className="voices-note">{t.noVoices}</p>}
        <EdgeHint />
        <div className="sliders">
          <div className="slider">
            <div className="slider__top">
              <label htmlFor="rate">{t.speed}</label>
              <span>{rateLabel}</span>
            </div>
            <input id="rate" type="range" min={5} max={20} value={Math.round(s.rate * 10)} onChange={(e) => updateSettings({ rate: Number(e.target.value) / 10, rateChosen: true })} />
          </div>
          <div className="slider">
            <div className="slider__top">
              <label htmlFor="pitch">{t.pitch}</label>
              <span>{pitchLabel}</span>
            </div>
            <input id="pitch" type="range" min={0} max={20} value={Math.round(s.pitch * 10)} onChange={(e) => updateSettings({ pitch: Number(e.target.value) / 10 })} />
          </div>
          <div className="slider">
            <div className="slider__top">
              <label htmlFor="vol">{t.volume}</label>
              <span>{Math.round(s.volume * 100)}%</span>
            </div>
            <input id="vol" type="range" min={0} max={100} value={Math.round(s.volume * 100)} onChange={(e) => updateSettings({ volume: Number(e.target.value) / 100 })} />
          </div>
        </div>
        {sp.cloud !== 'off' && (
          <label className="row row--sm card" style={{ padding: '4px 16px' }}>
            <span>
              <span className="row__title">{t.premiumVoice}</span>
              <span className="row__sub">
                {sp.cloud === 'exhausted' ? t.premiumVoiceUsedUp : sp.cloudRemaining !== null ? fmt(t.premiumVoiceLeft, { n: sp.cloudRemaining.toLocaleString() }) : t.premiumVoiceNote}
              </span>
            </span>
            <input className="sw" type="checkbox" role="switch" checked={s.premiumVoice} onChange={(e) => updateSettings({ premiumVoice: e.target.checked })} />
          </label>
        )}
        <label className="row row--sm card" style={{ padding: '4px 16px' }}>
          <span>
            <span className="row__title">{t.speedUpBusy}</span>
            <span className="row__sub">{t.speedUpBusySub}</span>
          </span>
          <input className="sw" type="checkbox" role="switch" checked={s.speedUpWhenBusy} onChange={(e) => updateSettings({ speedUpWhenBusy: e.target.checked, speedUpChosen: true })} />
        </label>
        <button type="button" className="btn btn--outline" onClick={test}>
          <Play />
          {t.testVoice}
        </button>
      </section>

      <section className="stack">
        <h2 className="section-title">{t.readSection}</h2>
        <div className="card" style={{ padding: '0 16px' }}>
          {(
            [
              ['chat', t.chatComments],
              ['gift', t.gifts],
              ['follow', t.newFollows],
              ['share', t.shares],
              ['join', t.viewersJoining],
              ['like', t.likes],
            ] as Array<[keyof typeof s.read, string]>
          ).map(([k, label]) => (
            <label key={k} className="row row--sm">
              <span className="row__title">{label}</span>
              <input className="sw" type="checkbox" role="switch" checked={s.read[k]} onChange={(e) => updateSettings({ read: { ...s.read, [k]: e.target.checked } })} />
            </label>
          ))}
        </div>
        <label htmlFor="fmt" className="label" style={{ color: 'var(--text)', fontSize: 15, marginTop: 4 }}>
          {t.formatLabel}
        </label>
        <input id="fmt" className="tx" type="text" value={s.format} onChange={(e) => updateSettings({ format: e.target.value })} />
        <p className="desc">{t.formatHelp}</p>
      </section>

      <section className="stack">
        <h2 className="section-title">{t.soundEffects}</h2>
        <div className="card" style={{ padding: '4px 16px' }}>
          {(
            [
              ['gift', t.sfxGift],
              ['follow', t.sfxFollow],
              ['share', t.sfxShare],
              ['chat', t.sfxChat],
            ] as Array<[keyof typeof s.sfx, string]>
          ).map(([k, label]) => (
            <div key={k} className="sfxrow">
              <span className="sfxrow__dot" style={{ background: SFX_COLOR[k] }} />
              <label htmlFor={`sfx-${k}`}>{label}</label>
              <select id={`sfx-${k}`} className="sel sel--sm" value={s.sfx[k]} onChange={(e) => updateSettings({ sfx: { ...s.sfx, [k]: e.target.value as SfxName } })}>
                {SFX_OPTIONS[k].map((o) => (
                  <option key={o} value={o}>
                    {t.sfxNames[o]}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="iconbtn"
                aria-label={fmt(t.preview, { name: label })}
                onClick={() => {
                  unlockAudio();
                  playSfx(s.sfx[k], s.volume);
                }}
              >
                <Play />
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="stack">
        <h2 className="section-title">{t.filters}</h2>
        <div className="card" style={{ padding: '0 16px' }}>
          {(
            [
              ['skipEmojiOnly', t.skipEmoji],
              ['skipLinks', t.skipLinks],
              ['dedupe', t.dedupe],
            ] as Array<['skipEmojiOnly' | 'skipLinks' | 'dedupe', string]>
          ).map(([k, label]) => (
            <label key={k} className="row row--sm">
              <span className="row__title">{label}</span>
              <input className="sw" type="checkbox" role="switch" checked={s.filters[k]} onChange={(e) => updateSettings({ filters: { ...s.filters, [k]: e.target.checked } })} />
            </label>
          ))}
        </div>
        <label htmlFor="maxlen" className="label" style={{ color: 'var(--text)', fontSize: 15, marginTop: 4 }}>
          {t.maxLength}
        </label>
        <select id="maxlen" className="sel" value={s.filters.maxLength} onChange={(e) => updateSettings({ filters: { ...s.filters, maxLength: Number(e.target.value) } })}>
          {[150, 80, 300].map((n) => (
            <option key={n} value={n}>
              {fmt(t.characters, { n })}
            </option>
          ))}
          <option value={0}>{t.noLimit}</option>
        </select>
        <label htmlFor="blk" className="label" style={{ color: 'var(--text)', fontSize: 15, marginTop: 4 }}>
          {t.blocklist}
        </label>
        <input id="blk" className="tx" type="text" placeholder={t.blocklistPlaceholder} value={s.filters.blocklist} onChange={(e) => updateSettings({ filters: { ...s.filters, blocklist: e.target.value } })} />
        <label htmlFor="pfx" className="label" style={{ color: 'var(--text)', fontSize: 15, marginTop: 4 }}>
          {t.prefix}
        </label>
        <input id="pfx" className="tx" type="text" placeholder={t.prefixPlaceholder} value={s.filters.prefix} onChange={(e) => updateSettings({ filters: { ...s.filters, prefix: e.target.value } })} />
      </section>

      <section className="stack">
        <h2 className="section-title">{t.queue}</h2>
        <label htmlFor="q" className="label" style={{ color: 'var(--text)', fontSize: 15 }}>
          {t.queueMax}
        </label>
        <select id="q" className="sel" value={s.queueMax} onChange={(e) => updateSettings({ queueMax: Number(e.target.value) })}>
          {[10, 5, 25].map((n) => (
            <option key={n} value={n}>
              {fmt(t.queueOption, { n })}
            </option>
          ))}
        </select>
        <p className="desc">{t.queueHelp}</p>
      </section>

      <section className="stack">
        <h2 className="section-title">{t.overlay}</h2>
        <p className="desc">{t.overlayHelp}</p>
        <div className="copy-url">
          <input className="tx" type="text" readOnly value={overlayUrl} onFocus={(e) => e.target.select()} aria-label={t.overlay} />
          <button type="button" className="btn" onClick={() => copy(overlayUrl, 'feed')}>
            {copied === 'feed' ? t.copied : t.copy}
          </button>
        </div>
        <label htmlFor="killfeed-url" className="label" style={{ color: 'var(--text)', fontSize: 15, marginTop: 8 }}>
          {t.killfeed}
        </label>
        <p className="desc">{t.killfeedHelp}</p>
        <div className="copy-url">
          <input id="killfeed-url" className="tx" type="text" readOnly value={killfeedUrl} onFocus={(e) => e.target.select()} />
          <button type="button" className="btn" onClick={() => copy(killfeedUrl, 'killfeed')}>
            {copied === 'killfeed' ? t.copied : t.copy}
          </button>
        </div>
        <p className="desc">{t.overlayUsesSlot}</p>
      </section>
    </div>
  );
}
